'use client';

import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<{ full_name: string; email: string; role: string; avatar_url?: string } | null>(null);
  const [profileLoadError, setProfileLoadError] = useState(false);

  const supabase = createClient();
  const mountedRef = useRef(true);
  const profileFetchingRef = useRef(false);

  // Fetch profile from DB — never throws, never signs out
  const fetchUserProfile = useCallback(async (userId: string, userEmail?: string): Promise<void> => {
    if (profileFetchingRef.current) return;
    profileFetchingRef.current = true;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('full_name, email, role, avatar_url')
        .eq('id', userId)
        .single();

      if (!mountedRef.current) return;

      if (data && !error) {
        setUserProfile({
          full_name: data.full_name || '',
          email: data.email || userEmail || '',
          role: data.role || 'user',
          avatar_url: data.avatar_url || '',
        });
        setProfileLoadError(false);
      } else {
        // Profile fetch failed — retry once after 1.5s
        // CRITICAL: do NOT sign out. Auth session is valid; DB may be slow.
        await new Promise<void>((resolve) => setTimeout(resolve, 1500));
        if (!mountedRef.current) return;

        const { data: retryData, error: retryError } = await supabase
          .from('user_profiles')
          .select('full_name, email, role, avatar_url')
          .eq('id', userId)
          .single();

        if (!mountedRef.current) return;

        if (retryData && !retryError) {
          setUserProfile({
            full_name: retryData.full_name || '',
            email: retryData.email || userEmail || '',
            role: retryData.role || 'user',
            avatar_url: retryData.avatar_url || '',
          });
          setProfileLoadError(false);
        } else {
          // Both attempts failed — show error UI but keep the auth session intact
          setProfileLoadError(true);
        }
      }
    } catch {
      if (mountedRef.current) {
        setProfileLoadError(true);
      }
    } finally {
      profileFetchingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual reload: clears error state and re-fetches profile without touching auth
  const reloadProfile = useCallback(() => {
    if (!user) return;
    setProfileLoadError(false);
    profileFetchingRef.current = false;
    fetchUserProfile(user.id, user.email ?? undefined);
  }, [user, fetchUserProfile]);

  useEffect(() => {
    mountedRef.current = true;

    const init = async () => {
      try {
        // STEP 1: Use getSession() first — it reads cookies synchronously and
        // restores the session instantly on page reload without a network round-trip.
        // This is the key fix: getUser() makes a network call and can fail/timeout
        // on reload, causing the session to appear lost.
        const { data: { session: currentSession } } = await supabase.auth.getSession();

        if (!mountedRef.current) return;

        if (currentSession?.user) {
          // Session found in cookies — set state immediately so UI renders
          setSession(currentSession);
          setUser(currentSession.user);
          setLoading(false);

          // Fetch profile in parallel (non-blocking for loading state)
          fetchUserProfile(currentSession.user.id, currentSession.user.email ?? undefined);

          // STEP 2: Validate the session server-side in the background.
          // If the token is expired, getUser() will trigger a refresh via the
          // middleware and return the refreshed user. We update state silently.
          supabase.auth.getUser().then(({ data: { user: validatedUser } }) => {
            if (!mountedRef.current) return;
            if (validatedUser && validatedUser.id !== currentSession.user.id) {
              // Edge case: user changed between getSession and getUser
              setUser(validatedUser);
            }
          }).catch(() => {
            // Network error during background validation — keep existing session state
          });
        } else {
          // No session in cookies — user is genuinely not logged in
          setSession(null);
          setUser(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
      }
    };

    init();

    // Listen for auth state changes AFTER init
    // onAuthStateChange fires for: SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED
    // We skip INITIAL_SESSION here because init() already handled it via getSession()
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mountedRef.current) return;

      // Skip INITIAL_SESSION — init() already read the session from cookies
      if (event === 'INITIAL_SESSION') return;

      const authUser = newSession?.user ?? null;
      setSession(newSession);
      setUser(authUser);

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        if (authUser) {
          profileFetchingRef.current = false; // allow fresh fetch
          fetchUserProfile(authUser.id, authUser.email ?? undefined);
        }
      } else if (event === 'SIGNED_OUT') {
        setUserProfile(null);
        setProfileLoadError(false);
        profileFetchingRef.current = false;
      }

      setLoading(false);
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) return null;
      return user;
    } catch {
      return null;
    }
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Refresh the cached user profile (call after profile updates)
  const refreshUserProfile = async () => {
    if (!user) return;
    profileFetchingRef.current = false; // force re-fetch
    await fetchUserProfile(user.id, user.email);
  };

  const value = {
    user,
    session,
    loading,
    userProfile,
    profileLoadError,
    reloadProfile,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {profileLoadError ? (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.7)', gap: '16px'
        }}>
          <p style={{ color: '#fff', fontSize: '16px', textAlign: 'center', maxWidth: '320px' }}>
            Unable to load your profile. Please try again.
          </p>
          <button
            onClick={reloadProfile}
            style={{
              padding: '10px 24px', borderRadius: '8px', border: 'none',
              background: '#4f46e5', color: '#fff', fontSize: '15px',
              fontWeight: 600, cursor: 'pointer'
            }}
          >
            Reload Profile
          </button>
        </div>
      ) : null}
      {children}
    </AuthContext.Provider>
  );
};
